import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getCategoriesForBusinessType } from './utils/constants';
import { recordProductActivity, ACTIVITY_TYPES } from './utils/productActivityTracker';
import { useExpirySweepLifecycle } from './features/operations/hooks/useExpirySweepLifecycle';
import { useInventoryCalculationLifecycle } from './features/inventory/hooks/useInventoryCalculationLifecycle';
import { useReportSyncLifecycle } from './features/sync/hooks/useReportSyncLifecycle';
import { useBackgroundSyncLifecycle } from './features/sync/hooks/useBackgroundSyncLifecycle';
import { useHybridRealtimeLifecycle } from './features/sync/hooks/useHybridRealtimeLifecycle';
import { useMirrorEntityRealtimeLifecycle } from './features/sync/hooks/useMirrorEntityRealtimeLifecycle';
import { registerDailyClosingDataProvider } from './domain/operations/dailyClosingOrchestrator';
import { useBusinessFieldPersistence } from './features/persistence/hooks/useBusinessFieldPersistence';
import { useBusinessSyncSetters } from './features/persistence/hooks/useBusinessSyncSetters';
import { useBusinessRefresh } from './features/persistence/hooks/useBusinessRefresh';
import { useBusinessLoad } from './features/persistence/hooks/useBusinessLoad';
import { useBusinessFieldKvSync } from './features/persistence/hooks/useBusinessFieldKvSync';
import { useBusinessBootstrap } from './features/persistence/hooks/useBusinessBootstrap';
import { useDataContextSlices } from './features/persistence/hooks/useDataContextSlices';
import { DataContextProviders } from './features/persistence/components/DataContextProviders';
import { resolveWorkerSessionsForBusiness } from './utils/workerSessionsResolve';
import { shouldLoadLocalBusinessData } from './utils/businessBootstrapUtils';
// useLotSystemIntegration - REMOVED: Not used in DataContext
import { calculateDaysUntilExpiry } from './utils/dateCalculations';
import { calculateProductData, updateProductUnified } from './utils/lotSystemUtils';
import {
  getBusinessInfo,
  getCurrentBusinessId,
  businessDataStorageKey,
  normalizeBusinessStorageId
} from './utils/unifiedDataHelpers';
import { getCurrentDateTime } from './utils/dateUtils';
import { readStoredSessionBusinessType } from './services/offlineAuthSession';
import dataChangeDetector from './services/dataChangeDetector.js';
import { isDeferDataChangeDetectorEnabled } from './infrastructure/startup/bootFeatureFlags';
import { shouldSkipLotResyncAfterBoot } from './infrastructure/startup/bootLotResyncGuard';
import { publishDataChanged, publishExpenseRecorded } from './infrastructure/events/legacyEventBridge';
import { afterExpenseCommit, syncEntities } from './services/cacheReadModel';
import invoiceService from './services/invoiceService.js';
import {
  mergePurchasesById,
  mergeProductsById,
  mergeSuppliersById,
  mergeLotsById,
  mergeEntitiesById,
  normalizeLotsField
} from './utils/businessStateSync';
import {
  readLotsSyncForBusiness
} from './utils/lotsReadSource';
import { purgeLegacyStandaloneDomainKeys } from './utils/localStorageHealth';
import { Expense } from './models/expenseModels';
import { syncExpensePaymentsFromExpensesWithCache } from './domain/persistence/expensePaymentSync';
import { kvGet, kvSet, kvRemove, kvGetRaw, kvSetRaw } from './infrastructure/storage/kvAccess';
import { scheduleDataChangeTrigger } from './utils/scheduleDataChangeTrigger';

export { useData } from './contexts/dataContext.js';

const EMPTY_FILTERED_CATEGORIES = Object.freeze({});

export const DataProvider = ({ children }) => {
  // EXISTING DATA ARRAYS
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [expiredProducts, setExpiredProducts] = useState([]);
  
  // INVOICE SYSTEM DATA
  const [invoices, setInvoices] = useState([]);
  const [invoicePayments, setInvoicePayments] = useState([]);
  
  // SUPERMARKET PAYMENT SYSTEM DATA
  const [supermarketPayments, setSupermarketPayments] = useState([]);
  const [supermarketInvoices, setSupermarketInvoices] = useState([]);
  const [supplierDebts, setSupplierDebts] = useState([]);
  
  // ACCOUNTS RECEIVABLE SYSTEM DATA
  const [customerDebts, setCustomerDebts] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  
  // EXPENSE SYSTEM DATA
  const [expenses, setExpenses] = useState([]);

  // RESTAURANT MODE DATA
  const [restaurantDishes, setRestaurantDishes] = useState([]);
  const [stockConsumptions, setStockConsumptions] = useState([]);
  const [restaurantTables, setRestaurantTables] = useState([]);
  const [openTickets, setOpenTickets] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [productionOrders, setProductionOrders] = useState([]);
  
  // NEW INTEGRATED DATA
  const [lots, setLots] = useState({});                    // Lot system data
  const [stockData, setStockData] = useState({});         // Stock calculations
  const [workerSessions, setWorkerSessions] = useState([]); // Worker session data
  const [notifications, setNotifications] = useState([]);   // System notifications
  const [reports, setReports] = useState({});              // Cached report data
  
  // INVENTORY CALCULATION DATA
  const [inventoryData, setInventoryData] = useState({
    inventoryBase: [],
    inventoryValue: {
      totalValue: 0,
      potentialProfit: 0,
      byLocation: {},
      byCategory: {},
      lastUpdated: null,
      source: 'initial'
    },
    categories: [],
    subcategories: [],
    types: [],
    statistics: {
      totalProducts: 0,
      productsWithStock: 0,
      productsWithoutStock: 0,
      totalValue: 0,
      calculationTime: 0
    },
    isCalculating: false,
    lastCalculated: null
  });
  
  // CONSOLIDATED DATA
  const [stockLocations, setStockLocations] = useState([]); // Additional stock locations
  const [persistentStockData, setPersistentStockData] = useState([]); // Persistent stock data
  const [offlineQueue, setOfflineQueue] = useState([]);     // Offline operations queue
  const [businessInfo, setBusinessInfo] = useState(null);   // Current business info
  const [userInfo, setUserInfo] = useState(null);           // Current user info
  const [metadata, setMetadata] = useState({});             // System metadata and flags
  
  // DISCREPANCY MANAGEMENT SYSTEM
  const [discrepancyManagement, setDiscrepancyManagement] = useState({
    selfDeviations: [],           // Type 1: Self-calculation deviations
    handoverDeviations: [],       // Type 2: Handover deviations
    workerTolerances: {},         // Monthly tolerance tracking per worker
    pendingReviews: [],           // Manager review queue
    auditTrail: [],               // Complete audit trail
    notifications: [],            // Manager notifications
    toleranceSystemEnabled: false // Toggle for tolerance system
  });

  // IOT SYSTEM DATA - REMOVED: Not used in any components, iotService manages its own state
  
  // BUSINESS STATE — type from session on first paint (restaurant POS must not flash AppHeader)
  const [currentBusinessId, setCurrentBusinessId] = useState(null);
  const [currentBusinessType, setCurrentBusinessType] = useState(() => {
    try {
      return readStoredSessionBusinessType();
    } catch {
      return null;
    }
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dataShellReady, setDataShellReady] = useState(false);
  const [ready, setReady] = useState(false);

  // Use ref to avoid timing issues with state updates
  const businessIdRef = useRef(null);
  const syncBusinessFieldsFromKvRef = useRef(null);
  const closingDataSourcesRef = useRef({});

  const businessLoadSetters = useMemo(
    () => ({
      setProducts,
      setSuppliers,
      setPurchases,
      setOrders,
      setCustomers,
      setSales,
      setExpiredProducts,
      setInvoices,
      setInvoicePayments,
      setSupermarketPayments,
      setSupermarketInvoices,
      setSupplierDebts,
      setCustomerDebts,
      setCustomerInvoices,
      setExpenses,
      setRestaurantDishes,
      setStockConsumptions,
      setRestaurantTables,
      setOpenTickets,
      setRecipes,
      setProductionOrders,
      setLots,
      setStockData,
      setWorkerSessions,
      setNotifications,
      setReports,
      setStockLocations,
      setPersistentStockData,
      setOfflineQueue,
      setBusinessInfo,
      setUserInfo,
      setMetadata,
      setDiscrepancyManagement
    }),
    []
  );

  const { loadBusinessData } = useBusinessLoad({
    setters: businessLoadSetters,
    setDataLoaded,
    setDataShellReady,
    setReady
  });

  /**
   * Drop every business entity from memory.
   * The provider stays mounted for the process lifetime, so logout must wipe the arrays:
   * otherwise business A stays in RAM on the login screen and the next load union-merges
   * its ids into business B.
   */
  const resetBusinessEntityState = useCallback(() => {
    setProducts([]);
    setSuppliers([]);
    setPurchases([]);
    setOrders([]);
    setCustomers([]);
    setSales([]);
    setExpiredProducts([]);
    setInvoices([]);
    setInvoicePayments([]);
    setSupermarketPayments([]);
    setSupermarketInvoices([]);
    setSupplierDebts([]);
    setCustomerDebts([]);
    setCustomerInvoices([]);
    setExpenses([]);
    setRestaurantDishes([]);
    setStockConsumptions([]);
    setRestaurantTables([]);
    setOpenTickets([]);
    setRecipes([]);
    setProductionOrders([]);
    setLots({});
    setStockData({});
    setWorkerSessions([]);
    setNotifications([]);
    setReports({});
    setStockLocations([]);
    setPersistentStockData([]);
    setOfflineQueue([]);
    setBusinessInfo(null);
    setUserInfo(null);
    setMetadata({});
    setDiscrepancyManagement({
      selfDeviations: [],
      handoverDeviations: [],
      workerTolerances: {},
      pendingReviews: [],
      auditTrail: [],
      notifications: [],
      toleranceSystemEnabled: false
    });
    setInventoryData({
      inventoryBase: [],
      inventoryValue: {
        totalValue: 0,
        potentialProfit: 0,
        byLocation: {},
        byCategory: {},
        lastUpdated: null,
        source: 'reset'
      },
      categories: [],
      subcategories: [],
      types: [],
      statistics: {
        totalProducts: 0,
        productsWithStock: 0,
        productsWithoutStock: 0,
        totalValue: 0,
        calculationTime: 0
      },
      isCalculating: false,
      lastCalculated: null
    });
  }, []);

  const {
    setSalesOnly,
    setPurchasesOnly,
    setLotsOnly,
    setCustomersOnly,
    setSuppliersOnly,
    setProductsOnly,
    setOrdersOnly,
    setWorkerSessionsOnly,
    refreshSalesFromStorage,
    refreshPurchasesFromStorage,
    refreshLotsFromStorage,
    refreshOrdersFromStorage,
    refreshSuppliersFromStorage,
    refreshInvoicesFromStorage,
    refreshExpensesFromStorage,
    refreshSupermarketPaymentsFromStorage,
    refreshInvoicePaymentsFromStorage,
    refreshSupermarketInvoicesFromStorage,
    refreshSupplierDebtsFromStorage,
    refreshCustomersFromStorage,
    refreshCustomerDebtsFromStorage,
    refreshExpiredProductsFromStorage,
    refreshNotificationsFromStorage,
    refreshDiscrepancyFromStorage,
    refreshWorkerSessionsFromStorage,
    refreshReportsFromStorage,
    refreshProductsFromStorage
  } = useBusinessRefresh({
    setters: {
      setProducts,
      setPurchases,
      setSales,
      setLots,
      setOrders,
      setSuppliers,
      setInvoices,
      setExpenses,
      setSupermarketPayments,
      setInvoicePayments,
      setSupermarketInvoices,
      setSupplierDebts,
      setCustomers,
      setCustomerDebts,
      setExpiredProducts,
      setNotifications,
      setDiscrepancyManagement,
      setWorkerSessions,
      setReports
    },
    currentBusinessId
  });

  const kvSyncSetters = useMemo(
    () => ({
      setSales,
      setCustomers,
      setPurchases,
      setProducts,
      setLots,
      setSuppliers,
      setStockData,
      setInvoices,
      setOrders,
      setExpenses,
      setInvoicePayments,
      setSupplierDebts,
      setCustomerDebts,
      setCustomerInvoices,
      setSupermarketPayments,
      setSupermarketInvoices,
      setExpiredProducts,
      setNotifications,
      setWorkerSessions,
      setReports,
      setDiscrepancyManagement,
      setStockLocations,
      setPersistentStockData,
      setOfflineQueue,
      setBusinessInfo,
      setUserInfo,
      setMetadata,
      setOpenTickets,
      setRestaurantTables,
      setRecipes,
      setProductionOrders,
      setStockConsumptions
    }),
    []
  );

  useBusinessFieldKvSync({
    businessIdRef,
    setters: kvSyncSetters,
    syncRef: syncBusinessFieldsFromKvRef
  });

  useEffect(() => {
    let unsub = () => {};
    void import('./infrastructure/storage/restaurantOpsLiveSync')
      .then(({ installRestaurantOpsLiveSync }) => {
        unsub = installRestaurantOpsLiveSync({
          getBusinessId: () => businessIdRef.current,
          setOpenTickets,
          setRestaurantTables
        });
      })
      .catch(() => {});
    return () => {
      unsub();
    };
  }, [businessIdRef, setOpenTickets, setRestaurantTables]);

  useBusinessBootstrap({
    loadBusinessData,
    syncBusinessFieldsFromKvRef,
    businessIdRef,
    setCurrentBusinessId,
    setCurrentBusinessType,
    setDataLoaded,
    setDataShellReady,
    setReady,
    resetBusinessEntityState
  });

  // Lots aus entity_* / Warm-Cache (Phase 26: kein KV-Blob mehr nötig)
  const syncLotsWithDataContext = useCallback(async () => {
    const businessId = businessIdRef.current || currentBusinessId;
    if (!shouldLoadLocalBusinessData(businessId)) {
      return;
    }

    try {
      purgeLegacyStandaloneDomainKeys(businessId);
      const { isEntityStoreNoKvBlobEnabled } = await import(
        './infrastructure/storage/businessPayloadMeta'
      );
      let nextLots;
      if (isEntityStoreNoKvBlobEnabled()) {
        // refreshLotsReadCache merges warm∪entity (fresher wins) — safe during in-flight writes
        const { refreshLotsReadCache } = await import('./utils/lotsReadSource');
        nextLots = await refreshLotsReadCache(businessId);
      } else {
        nextLots = readLotsSyncForBusiness(businessId);
      }
      setLots(Array.isArray(nextLots) ? nextLots : normalizeLotsField(nextLots));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[DataContext] syncLotsFromBlob failed:', error?.message || error);
      }
    }
  }, [currentBusinessId]);

  useEffect(() => {
    if (currentBusinessId && shouldLoadLocalBusinessData(currentBusinessId)) {
      if (shouldSkipLotResyncAfterBoot()) return;
      syncLotsWithDataContext();
    }
  }, [currentBusinessId, syncLotsWithDataContext]);

  // Initialize data change detection
  useEffect(() => {
    const initDetector = () => dataChangeDetector.initialize();
    if (isDeferDataChangeDetectorEnabled()) {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(initDetector, { timeout: 3000 });
      } else {
        setTimeout(initDetector, 0);
      }
    } else {
      initDetector();
    }

    return () => {
      dataChangeDetector.cleanup();
    };
  }, []);

  // Listen for lot updates from other components (debounce — lot edits can multi-fire)
  useEffect(() => {
    let timer = null;
    const syncLots = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        syncLotsWithDataContext();
      }, 120);
    };

    const onDomainEvent = (event) => {
      const type = event?.detail?.type;
      if (
        type === 'inventory_updated' ||
        type === 'purchases_updated' ||
        type === 'sales_updated'
      ) {
        syncLots();
      }
    };

    window.addEventListener('elkheir:domain-event', onDomainEvent);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
    };
  }, [syncLotsWithDataContext]);

  const syncWorkerSessionsFromKv = useCallback(async () => {
    const businessId = businessIdRef.current || currentBusinessId;
    if (!shouldLoadLocalBusinessData(businessId)) {
      return;
    }
    try {
      const { refreshWorkerSessionsReadCache } = await import('./utils/workerSessionsReadSource');
      const fromEntity = await refreshWorkerSessionsReadCache(businessId);
      const sessions = resolveWorkerSessionsForBusiness(businessId, fromEntity);
      setWorkerSessions(Array.isArray(sessions) ? sessions : []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[DataContext] syncWorkerSessionsFromKv failed:',
          error?.message || error
        );
      }
    }
  }, [currentBusinessId]);

  useEffect(() => {
    const onDomainEvent = (event) => {
      if (event?.detail?.type === 'worker_session_updated') {
        syncWorkerSessionsFromKv();
      }
    };

    window.addEventListener('elkheir:domain-event', onDomainEvent);

    return () => {
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
    };
  }, [syncWorkerSessionsFromKv]);

  // Business status monitoring — works with local storage slugs (business_…) via cloudId
  const checkBusinessStatus = useCallback(async () => {
    const businessId = businessIdRef.current;
    if (!businessId || businessId === 'admin_user' || String(businessId).startsWith('admin-')) {
      return;
    }

    // Check if business status monitoring is disabled
    if (kvGetRaw('disableBusinessStatusCheck') === 'true') {
      return;
    }

    try {
      const { businessService, isSupabaseConfigured } = await import('./services/supabaseService');
      if (!isSupabaseConfigured()) {
        return;
      }

      // Prefer cloud businesses.id when dual (local slug + cloudId)
      let statusId = businessId;
      try {
        const sessionBiz = kvGet('currentUser')?.business || kvGet('currentBusiness');
        if (sessionBiz?.cloudId) {
          statusId = String(sessionBiz.cloudId);
        } else {
          const { resolveSupabaseBusinessId } = await import('./utils/businessTypeCloud');
          statusId = resolveSupabaseBusinessId(businessId) || businessId;
        }
      } catch {
        statusId = businessId;
      }

      const result = await businessService.getBusinessStatus(statusId);

      if (result.success && result.data) {
        const business = result.data;

        if (business.status === 'suspended') {
          // Drop the cached payload, then end the session through the normal logout
          // path — role/keep-alive/warm-cache teardown must not be skipped here (RL03).
          const suspendedBusinessId = getCurrentBusinessId();
          if (suspendedBusinessId) {
            const ck = businessDataStorageKey(suspendedBusinessId);
            if (ck) kvRemove(ck);
            const lk = `business_${suspendedBusinessId}_data`;
            if (lk !== ck) kvRemove(lk);
          }
          const { completeLogout } = await import('./services/completeLogoutService');
          await completeLogout({ reload: false });
        }
      } else if (result.skipRetry) {
        // Don't retry if it's a connection issue or offline
        return;
      }
    } catch (error) {
    }
  }, []);


  const { triggerExpiredProductsProcessing } = useExpirySweepLifecycle({
    currentBusinessId,
    dataLoaded,
    products,
    lots,
    expiredProducts,
    setExpiredProducts,
    setProducts,
    setLots,
    setStockData
  });

  const { calculateInventoryData } = useInventoryCalculationLifecycle({
    dataLoaded,
    products,
    lots,
    purchases,
    sales,
    inventoryData,
    setInventoryData
  });

  const { scheduleReportSyncAfterSave, syncReports } = useReportSyncLifecycle({
    currentBusinessId,
    products,
    sales,
    purchases,
    orders,
    customers,
    suppliers,
    expiredProducts
  });

  useEffect(() => {
    closingDataSourcesRef.current = {
      sales,
      purchases,
      expenses,
      supermarketPayments,
      businessInfo,
      workerSessions,
      discrepancyManagement,
      supplierDebts,
      customerDebts,
      inventoryData,
      products,
      lots,
      expiredProducts,
      supermarketInvoices
    };
  }, [
    sales,
    purchases,
    expenses,
    supermarketPayments,
    businessInfo,
    workerSessions,
    discrepancyManagement,
    supplierDebts,
    customerDebts,
    inventoryData,
    products,
    lots,
    expiredProducts,
    supermarketInvoices
  ]);

  const getClosingData = useCallback(() => closingDataSourcesRef.current, []);

  useEffect(() => {
    registerDailyClosingDataProvider(getClosingData);
    return () => registerDailyClosingDataProvider(null);
  }, [getClosingData]);

  useBackgroundSyncLifecycle({
    enabled: dataLoaded && shouldLoadLocalBusinessData(currentBusinessId),
    getClosingData,
    checkBusinessStatus,
    businessId: currentBusinessId
  });

  // Phase 3 — live Hybrid catalog (no-op unless cloud_plan=hybrid)
  useHybridRealtimeLifecycle({
    enabled: dataLoaded && shouldLoadLocalBusinessData(currentBusinessId),
    businessId: currentBusinessId
  });

  // Mirror sales/purchases realtime (manager remote history + dashboard)
  useMirrorEntityRealtimeLifecycle({
    enabled: dataLoaded && shouldLoadLocalBusinessData(currentBusinessId),
    businessId: currentBusinessId
  });

  const {
    enqueueBusinessFieldSave,
    enqueueBusinessFieldsBatchSave,
    resolveBusinessIdForSave
  } = useBusinessFieldPersistence({
    currentBusinessId,
    businessIdRef,
    scheduleReportSyncAfterSave
  });

  const {
    setProductsWithSync,
    setPurchasesWithSync,
    setSalesWithSync,
    setSuppliersWithSync,
    setOrdersWithSync,
    setCustomersWithSync,
    setLotsWithSync,
    setExpiredProductsWithSync,
    setInvoicesWithSync,
    setInvoicePaymentsWithSync,
    setSupermarketPaymentsWithSync,
    setSupermarketInvoicesWithSync,
    setSupplierDebtsWithSync,
    setCustomerDebtsWithSync,
    setCustomerInvoicesWithSync,
    setExpensesWithSync,
    setRestaurantDishesWithSync,
    setStockConsumptionsWithSync,
    setRestaurantTablesWithSync,
    setOpenTicketsWithSync,
    setRecipesWithSync,
    setProductionOrdersWithSync,
    setStockDataWithSync,
    setWorkerSessionsWithSync,
    setNotificationsWithSync,
    setReportsWithSync,
    setStockLocationsWithSync,
    setPersistentStockDataWithSync,
    setOfflineQueueWithSync,
    setMetadataWithSync,
    setUserInfoWithSync,
    setBusinessInfoWithSync,
    setDiscrepancyManagementWithSync
  } = useBusinessSyncSetters({
    setters: {
      setProducts,
      setPurchases,
      setSales,
      setSuppliers,
      setOrders,
      setCustomers,
      setLots,
      setExpiredProducts,
      setInvoices,
      setInvoicePayments,
      setSupermarketPayments,
      setSupermarketInvoices,
      setSupplierDebts,
      setCustomerDebts,
      setCustomerInvoices,
      setExpenses,
      setRestaurantDishes,
      setStockConsumptions,
      setRestaurantTables,
      setOpenTickets,
      setRecipes,
      setProductionOrders,
      setStockData,
      setWorkerSessions,
      setNotifications,
      setReports,
      setStockLocations,
      setPersistentStockData,
      setOfflineQueue,
      setMetadata,
      setUserInfo,
      setBusinessInfo,
      setDiscrepancyManagement
    },
    enqueueBusinessFieldSave,
    enqueueBusinessFieldsBatchSave,
    resolveBusinessIdForSave,
    scheduleDataChangeTrigger,
        products,
    currentBusinessId,
    invoiceService,
    Expense,
    getCurrentDateTime,
    businessDataStorageKey
  });

  const persistSupermarketPartialPayment = useCallback(
    async (nextPayments, updatedInvoices) => {
      const bid = resolveBusinessIdForSave();
      if (!bid) return false;

      const payments = Array.isArray(nextPayments) ? nextPayments : [];
      const rawInvoices = Array.isArray(updatedInvoices) ? updatedInvoices : [];
      const { default: invoiceService } = await import('./services/invoiceService');
      const invoices = invoiceService.consolidateInvoicesInMemory(rawInvoices);

      const currentPayments = Array.isArray(supermarketPayments) ? supermarketPayments : [];
      const existingPaymentIds = new Set(
        currentPayments.map((entry) => entry?.id).filter(Boolean)
      );
      const brandNewPayments = payments.filter(
        (entry) => entry?.id && !existingPaymentIds.has(entry.id)
      );
      if (payments.length > currentPayments.length && brandNewPayments.length === 0) {
        return true;
      }

      let previousPayments;
      let previousInvoices;
      setSupermarketPayments((prev) => {
        previousPayments = prev;
        return payments;
      });
      setSupermarketInvoices((prev) => {
        previousInvoices = prev;
        return invoices;
      });

      try {
        const { persistSupermarketPartialPayment: persistPayment } = await import(
          './infrastructure/storage/businessPayloadWrite'
        );
        await persistPayment(bid, {
          supermarketPayments: payments,
          supermarketInvoices: invoices
        });
        // Do not mirror supermarketPayments/Invoices into InstantCache (EPHEMERAL).
        // Typed finance facts — Manager / Bilan / Payment report stay aligned
        try {
          const { publishSupplierPaymentRecorded } = await import(
            './infrastructure/events/legacyEventBridge'
          );
          const { afterSupplierFinanceCommit } = await import('./services/cacheReadModel');
          publishSupplierPaymentRecorded({
            businessId: bid,
            paymentsCount: payments.length,
            invoicesCount: invoices.length,
            source: 'persistSupermarketPartialPayment'
          });
          afterSupplierFinanceCommit('supplier_payment_recorded');
        } catch (notifyErr) {
          console.warn('[DataContext] supplier finance notify:', notifyErr?.message || notifyErr);
        }
        return true;
      } catch (err) {
        console.error('[DataContext] persistSupermarketPartialPayment failed:', err?.message || err);
        if (previousPayments !== undefined) setSupermarketPayments(previousPayments);
        if (previousInvoices !== undefined) setSupermarketInvoices(previousInvoices);
        return false;
      }
    },
    [currentBusinessId]
  );

  const persistNewExpense = useCallback(
    async (expense) => {
      const bid = resolveBusinessIdForSave();
      if (!bid || !expense) return false;

      const currentExpenses = Array.isArray(expenses) ? expenses : [];
      if (expense?.id && currentExpenses.some((entry) => entry?.id === expense.id)) {
        return true;
      }

      const nextExpenses = [...currentExpenses, expense];
      const currentPayments = Array.isArray(supermarketPayments) ? supermarketPayments : [];
      const nextPayments = syncExpensePaymentsFromExpensesWithCache(
        nextExpenses,
        currentPayments,
        bid
      );

      let previousExpenses;
      let previousPayments;
      setExpenses((prev) => {
        previousExpenses = prev;
        return nextExpenses;
      });
      setSupermarketPayments((prev) => {
        previousPayments = prev;
        return nextPayments;
      });

      try {
        const { persistExpenseWithPayments } = await import(
          './infrastructure/storage/businessPayloadWrite'
        );
        await persistExpenseWithPayments(bid, {
          expenses: nextExpenses,
          supermarketPayments: nextPayments
        });
        // Silent snapshot — do not updatePartialData (that notifies + can feel like full recalc).
        syncEntities({
          expenses: nextExpenses,
          supermarketPayments: nextPayments
        });
        // Idle warm after return — Enregistrer must not wait on Manager/Reports.
        queueMicrotask(() => {
          try {
            publishExpenseRecorded({
              businessId: bid,
              expenseId: expense?.id || null,
              action: 'create'
            });
            afterExpenseCommit('expense_recorded');
          } catch (notifyErr) {
            console.warn('[DataContext] expense finance notify:', notifyErr?.message || notifyErr);
          }
        });
        return true;
      } catch (err) {
        console.error('[DataContext] persistNewExpense failed:', err?.message || err);
        if (previousExpenses !== undefined) setExpenses(previousExpenses);
        if (previousPayments !== undefined) setSupermarketPayments(previousPayments);
        return false;
      }
    },
    [currentBusinessId, expenses, supermarketPayments]
  );

  const persistUpdatedExpense = useCallback(
    async (expense) => {
      const bid = resolveBusinessIdForSave();
      if (!bid || !expense?.id) return false;

      const currentExpenses = Array.isArray(expenses) ? expenses : [];
      const index = currentExpenses.findIndex((entry) => entry?.id === expense.id);
      if (index < 0) return false;

      const nextExpenses = currentExpenses.slice();
      nextExpenses[index] = expense;
      const currentPayments = Array.isArray(supermarketPayments) ? supermarketPayments : [];
      const nextPayments = syncExpensePaymentsFromExpensesWithCache(
        nextExpenses,
        currentPayments,
        bid
      );

      let previousExpenses;
      let previousPayments;
      setExpenses((prev) => {
        previousExpenses = prev;
        return nextExpenses;
      });
      setSupermarketPayments((prev) => {
        previousPayments = prev;
        return nextPayments;
      });

      try {
        const { persistExpenseWithPayments } = await import(
          './infrastructure/storage/businessPayloadWrite'
        );
        await persistExpenseWithPayments(bid, {
          expenses: nextExpenses,
          supermarketPayments: nextPayments
        });
        syncEntities({
          expenses: nextExpenses,
          supermarketPayments: nextPayments
        });
        queueMicrotask(() => {
          try {
            publishExpenseRecorded({
              businessId: bid,
              expenseId: expense.id,
              action: 'update'
            });
            afterExpenseCommit('expense_recorded');
          } catch (notifyErr) {
            console.warn('[DataContext] expense finance notify:', notifyErr?.message || notifyErr);
          }
        });
        return true;
      } catch (err) {
        console.error('[DataContext] persistUpdatedExpense failed:', err?.message || err);
        if (previousExpenses !== undefined) setExpenses(previousExpenses);
        if (previousPayments !== undefined) setSupermarketPayments(previousPayments);
        return false;
      }
    },
    [currentBusinessId, expenses, supermarketPayments]
  );

  // setIotDataWithSync - REMOVED: iotData state removed

  // Stable categories object — a fresh {} / filter result every render was busting
  // BusinessMetaContext and re-rendering Inventaire / payment-report tables (vibration).
  const filteredCategories = useMemo(
    () =>
      currentBusinessType
        ? getCategoriesForBusinessType(currentBusinessType)
        : EMPTY_FILTERED_CATEGORIES,
    [currentBusinessType]
  );

  // PaymentService Integration - Setze DataContext fÃ¼r PaymentService
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.dataContext = {
        invoices,
        setInvoices: setInvoicesWithSync,
        supermarketPayments,
        setSupermarketPayments: setSupermarketPaymentsWithSync,
        supermarketInvoices,
        setSupermarketInvoices: setSupermarketInvoicesWithSync,
        supplierDebts,
        setSupplierDebts: setSupplierDebtsWithSync,
        customers,
        suppliers,
        products,
        sales,
        purchases,
        orders,
        lots,
        // Debug function to check invoices
        debugInvoices: () => {


          // Invoices details
          return invoices;
        },
        // Debug function to check if invoice exists
        findInvoice: (invoiceId) => {
          const found = invoices.find(inv => inv.id === invoiceId);

          return found;
        }
      };
    }
  }, [invoices, customers, suppliers, products, sales, purchases, orders, lots]);

  const dataSlices = useDataContextSlices({
    products,
    suppliers,
    purchases,
    orders,
    customers,
    sales,
    lots,
    expiredProducts,
    invoices,
    invoicePayments,
    supermarketPayments,
    supermarketInvoices,
    supplierDebts,
    customerDebts,
    customerInvoices,
    expenses,
    restaurantDishes,
    stockConsumptions,
    restaurantTables,
    openTickets,
    recipes,
    productionOrders,
    stockData,
    workerSessions,
    notifications,
    reports,
    stockLocations,
    persistentStockData,
    offlineQueue,
    businessInfo,
    userInfo,
    metadata,
    discrepancyManagement,
    inventoryData,
    currentBusinessType,
    filteredCategories,
    currentBusinessId,
    dataLoaded,
    dataShellReady,
    ready,
    setProductsWithSync,
    setProductsOnly,
    refreshProductsFromStorage,
    setSuppliersWithSync,
    setSuppliersOnly,
    refreshSuppliersFromStorage,
    setPurchasesWithSync,
    setPurchasesOnly,
    refreshPurchasesFromStorage,
    setOrdersWithSync,
    setOrdersOnly,
    refreshOrdersFromStorage,
    setCustomersWithSync,
    setCustomersOnly,
    refreshCustomersFromStorage,
    refreshCustomerDebtsFromStorage,
    setSalesWithSync,
    setSalesOnly,
    refreshSalesFromStorage,
    setLotsWithSync,
    setLotsOnly,
    refreshLotsFromStorage,
    setExpiredProductsWithSync,
    triggerExpiredProductsProcessing,
    setInvoicesWithSync,
    setInvoicePaymentsWithSync,
    setSupermarketPaymentsWithSync,
    setSupermarketInvoicesWithSync,
    setSupplierDebtsWithSync,
    setCustomerDebtsWithSync,
    setCustomerInvoicesWithSync,
    setExpensesWithSync,
    setRestaurantDishesWithSync,
    setStockConsumptionsWithSync,
    setRestaurantTablesWithSync,
    setOpenTicketsWithSync,
    setRecipesWithSync,
    setProductionOrdersWithSync,
    syncLotsWithDataContext,
    setStockDataWithSync,
    setWorkerSessionsWithSync,
    setWorkerSessionsOnly,
    refreshWorkerSessionsFromStorage,
    syncWorkerSessionsFromKv,
    setNotificationsWithSync,
    setReportsWithSync,
    setInventoryData,
    calculateInventoryData,
    setStockLocationsWithSync,
    setPersistentStockDataWithSync,
    setOfflineQueueWithSync,
    setBusinessInfoWithSync,
    setUserInfoWithSync,
    setMetadataWithSync,
    setDiscrepancyManagementWithSync,
    persistSupermarketPartialPayment,
    persistNewExpense,
    persistUpdatedExpense,
    refreshInvoicesFromStorage,
    refreshExpensesFromStorage,
    refreshSupermarketPaymentsFromStorage,
    refreshInvoicePaymentsFromStorage,
    refreshSupermarketInvoicesFromStorage,
    refreshSupplierDebtsFromStorage,
    refreshExpiredProductsFromStorage,
    refreshNotificationsFromStorage,
    refreshDiscrepancyFromStorage,
    refreshReportsFromStorage,
    syncReports
  });

  // Dispatch lightweight data change events (counts only — no full payload cloning)
  useEffect(() => {
    if (!ready) return undefined;
    if (products.length === 0 && sales.length === 0 && purchases.length === 0) {
      return undefined;
    }

    const lotsCount = Array.isArray(lots) ? lots.length : Object.keys(lots || {}).length;

    const timer = setTimeout(() => {
      publishDataChanged({
        dataType: 'all',
        changeType: 'modified',
        counts: {
          products: products.length,
          sales: sales.length,
          purchases: purchases.length,
          orders: orders.length,
          customers: customers.length,
          suppliers: suppliers.length,
          lots: lotsCount
        }
      });
    }, 200);

    return () => clearTimeout(timer);
  }, [
    ready,
    products.length,
    sales.length,
    purchases.length,
    orders.length,
    customers.length,
    suppliers.length,
    // Count only — lots object identity changes on every lot write (PERF-08/lots dep).
    Array.isArray(lots) ? lots.length : Object.keys(lots || {}).length
  ]);

  // Listen for lot updates to refresh DataContext and instant cache
  useEffect(() => {
    const refreshLotsAndStock = async (detail = {}) => {
      if (!currentBusinessId) return;
      try {
        await refreshLotsFromStorage(currentBusinessId);
        const { readStockDataFromBestSource } = await import('./utils/stockDataReadSource');
        const stockDoc = await readStockDataFromBestSource(currentBusinessId);
        if (stockDoc && Object.keys(stockDoc).length > 0) {
          setStockData(stockDoc);
        }
      } catch {
        /* ignore */
      }
    };

    const onDomainEvent = async (event) => {
      if (event?.detail?.type !== 'inventory_updated') return;
      const detail = event?.detail?.payload || {};
      if (
        detail.source === 'lotUpdate' ||
        detail.source === 'lotMigration' ||
        detail.forceRefresh ||
        detail.action === 'stockRecalculated'
      ) {
        await refreshLotsAndStock(detail);
      }
    };

    window.addEventListener('elkheir:domain-event', onDomainEvent);

    return () => {
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
    };
  }, [currentBusinessId, refreshLotsFromStorage]);

  return (
    <DataContextProviders slices={dataSlices}>{children}</DataContextProviders>
  );
};
 
